import type { Immutable, Nullable, Rect, Vector3 } from '@zephyr3d/base';
import { type Vector4, type TypedArray, type IEventTarget, Observable, Z_CONVENTION } from '@zephyr3d/base';
import type { ITimer } from './timer';
import { CPUTimer } from './timer';
import type {
  BlendingState,
  ColorState,
  DepthState,
  RasterizerState,
  RenderStateSet,
  StencilState
} from './render_states';
import type {
  FrameBufferOptions,
  SamplerOptions,
  TextureSampler,
  Texture2D,
  Texture3D,
  TextureCube,
  VertexLayout,
  GPUDataBuffer,
  FrameBuffer,
  GPUProgram,
  GPUObject,
  StructuredBuffer,
  BindGroupLayout,
  BindGroup,
  IndexBuffer,
  TextureVideo,
  TextureMipmapData,
  TextureImageElement,
  Texture2DArray,
  TextureCreationOptions,
  BufferCreationOptions,
  BufferUsage,
  VertexSemantic,
  VertexAttribFormat,
  VertexLayoutOptions,
  BaseTexture,
  RenderBundle
} from './gpuobject';
import {
  GPUResourceUsageFlags,
  makeVertexBufferType,
  getVertexFormatSize,
  getVertexAttribFormat
} from './gpuobject';
import type { PBComputeOptions, PBRenderOptions, PBStructTypeInfo } from './builder';
import { ProgramBuilder } from './builder';
import type {
  DataType,
  PrimitiveType,
  TextureFormat,
  GPUObjectList,
  FrameInfo,
  DeviceCaps,
  GPUProgramConstructParams,
  AbstractDevice,
  DeviceOptions,
  DeviceEventMap,
  DeviceViewport,
  DrawTextLayoutOptions,
  FrameBufferClearColors,
  TimestampQueryFilter,
  TimestampQueryHandle,
  TimestampQueryOptions,
  TimestampQueryResult
} from './base_types';
import { DrawText } from './helpers';
import { Pool } from './pool';

let _loggedZConvention = false;

/**
 * The device backend interface
 * @public
 */
export interface DeviceBackend {
  typeName(): string;
  supported(): Promise<boolean>;
  createDevice(cvs: HTMLCanvasElement, options?: DeviceOptions): Promise<Nullable<AbstractDevice>>;
}

type DeviceState = {
  framebuffer: Nullable<FrameBuffer>;
  windowOrderReversed: boolean;
  viewport: DeviceViewport;
  scissor: DeviceViewport;
  renderStateSet: Nullable<RenderStateSet>;
  program: Nullable<GPUProgram>;
  vertexLayout: Nullable<VertexLayout>;
  bindGroups: [BindGroup, Nullable<Iterable<number>>][];
};

type ResizeCallback = (
  cssWidth: number,
  cssHeight: number,
  deviceWidth: number,
  deviceHeight: number
) => void;

class ResizeHandler {
  private readonly _canvas: HTMLCanvasElement;
  private readonly _dpr: number;
  private _cssWidth: number;
  private _cssHeight: number;
  private _deviceWidth: number;
  private _deviceHeight: number;

  private _resizeObserver!: ResizeObserver;
  private _mutationObserver!: MutationObserver;
  private _onResizeCallback: ResizeCallback;

  constructor(canvas: HTMLCanvasElement, onResize: ResizeCallback, dpr: number) {
    this._canvas = canvas;
    this._dpr = dpr;
    this._cssWidth = 0;
    this._cssHeight = 0;
    this._deviceWidth = 0;
    this._deviceHeight = 0;
    this._onResizeCallback = onResize;
  }

  init() {
    const canvas = this._canvas;
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        this._handleResizeEntry(entry);
      });
      try {
        (this._resizeObserver as any).observe(canvas);
      } catch {
        this._resizeObserver.observe(canvas);
      }
    } else {
      if (window.MutationObserver) {
        this._mutationObserver = new MutationObserver((mutations) => {
          if (mutations.length > 0) {
            this._handleLegacyResize();
          }
        });
        this._mutationObserver.observe(canvas, { attributes: true, attributeFilter: ['style'] });
      }
      window.addEventListener('resize', this._handleLegacyResize);
      this._handleLegacyResize();
    }
  }

  private _handleResizeEntry(_entry: ResizeObserverEntry) {
    // CSS pixel
    const boxSize = Array.isArray(_entry.contentBoxSize) ? _entry.contentBoxSize[0] : _entry.contentBoxSize;
    const cssWidth = boxSize?.inlineSize ?? this._canvas.clientWidth;
    const cssHeight = boxSize?.blockSize ?? this._canvas.clientHeight;

    // Device pixel
    /*
    let deviceWidth: number;
    let deviceHeight: number;

    const dpBox = (entry as any).devicePixelContentBoxSize?.[0];
    if (dpBox) {
      deviceWidth = dpBox.inlineSize;
      deviceHeight = dpBox.blockSize;
    } else {
      deviceWidth = Math.round(cssWidth * this._dpr);
      deviceHeight = Math.round(cssHeight * this._dpr);
    }
    */
    const deviceWidth = cssWidth * this._dpr;
    const deviceHeight = cssHeight * this._dpr;

    if (
      cssWidth === this._cssWidth &&
      cssHeight === this._cssHeight &&
      deviceWidth === this._deviceWidth &&
      deviceHeight === this._deviceHeight
    ) {
      return;
    }

    this._cssWidth = cssWidth;
    this._cssHeight = cssHeight;
    this._deviceWidth = deviceWidth;
    this._deviceHeight = deviceHeight;

    this._onResizeCallback(cssWidth, cssHeight, deviceWidth, deviceHeight);
  }

  private _handleLegacyResize = () => {
    const cssWidth = this._canvas.clientWidth;
    const cssHeight = this._canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    const deviceWidth = Math.round(cssWidth * dpr);
    const deviceHeight = Math.round(cssHeight * dpr);

    if (
      cssWidth === this._cssWidth &&
      cssHeight === this._cssHeight &&
      deviceWidth === this._deviceWidth &&
      deviceHeight === this._deviceHeight
    ) {
      return;
    }

    this._cssWidth = cssWidth;
    this._cssHeight = cssHeight;
    this._deviceWidth = deviceWidth;
    this._deviceHeight = deviceHeight;

    this._onResizeCallback(cssWidth, cssHeight, deviceWidth, deviceHeight);
  };

  public dispose() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }
    window.removeEventListener('resize', this._handleLegacyResize);
  }
}

/**
 * Base class for rendering device
 * @public
 */
export abstract class BaseDevice extends Observable<DeviceEventMap> {
  protected _dpr: number;
  protected _canvas: HTMLCanvasElement;
  protected _canvasClientWidth: number;
  protected _canvasClientHeight: number;
  protected _gpuObjectList: GPUObjectList;
  protected _gpuMemCost: number;
  protected _disposeObjectList: GPUObject[];
  protected _beginFrameTime: number;
  protected _endFrameTime: number;
  protected _fixedFrameTime: Nullable<number>;
  protected _fixedFrameClock: number;
  protected _frameInfo: FrameInfo;
  protected _cpuTimer: CPUTimer;
  protected _gpuTimer: Nullable<ITimer>;
  protected _runningLoop: Nullable<number>;
  protected _fpsCounter: { time: number; frame: number };
  protected _runLoopFunc: Nullable<(device: AbstractDevice) => void>;
  protected _backend: DeviceBackend;
  protected _beginFrameCounter: number;
  protected _programBuilder: ProgramBuilder;
  protected _poolMap: Map<string | symbol, Pool>;
  protected _defaultPoolKey: symbol;
  protected _temporalFramebuffer: boolean;
  protected _vSync: boolean;
  private readonly _resizer: ResizeHandler;
  private readonly _stateStack: DeviceState[];
  constructor(cvs: HTMLCanvasElement, backend: DeviceBackend, dpr?: number) {
    super();
    if (!_loggedZConvention) {
      _loggedZConvention = true;
      console.info(`[Zephyr3D] Z Convention: ${Z_CONVENTION}`);
    }
    this._dpr = dpr ?? window.devicePixelRatio ?? 1;
    this._backend = backend;
    this._gpuObjectList = {
      stacks: new Map(),
      textures: [],
      samplers: [],
      buffers: [],
      programs: [],
      framebuffers: [],
      vertexArrayObjects: [],
      bindGroups: []
    };
    this._canvas = cvs;
    this._canvas.setAttribute('tabindex', '1');
    this._canvasClientWidth = cvs.clientWidth;
    this._canvasClientHeight = cvs.clientHeight;
    this._gpuMemCost = 0;
    this._disposeObjectList = [];
    this._beginFrameTime = 0;
    this._endFrameTime = 0;
    this._fixedFrameTime = null;
    this._fixedFrameClock = 0;
    this._runLoopFunc = null;
    this._frameInfo = {
      frameCounter: 0,
      frameTimestamp: 0,
      elapsedTimeCPU: 0,
      elapsedTimeGPU: 0,
      elapsedFrame: 0,
      elapsedOverall: 0,
      FPS: 0,
      drawCalls: 0,
      computeCalls: 0,
      nextFrameCall: [],
      nextFrameCallNext: []
    };
    this._programBuilder = new ProgramBuilder(this);
    this._cpuTimer = new CPUTimer();
    this._gpuTimer = null;
    this._runningLoop = null;
    this._fpsCounter = { time: 0, frame: 0 };
    this._stateStack = [];
    this._beginFrameCounter = 0;
    this._poolMap = new Map();
    this._defaultPoolKey = Symbol('defaultPool');
    this._poolMap.set(this._defaultPoolKey, new Pool(this, this._defaultPoolKey));
    this._temporalFramebuffer = false;
    this._temporalFramebuffer = false;
    this._vSync = true;
    //this._registerEventHandlers();
    this._resizer = new ResizeHandler(
      this._canvas,
      (cssWidth, cssHeight, deviceWidth, deviceHeight) => {
        this.dispatchEvent('resize', cssWidth, cssHeight, deviceWidth, deviceHeight);
      },
      this._dpr
    );
  }
  getScaleX() {
    return this._canvas.clientWidth === 0 ? 1 : this._canvas.width / this._canvas.clientWidth;
  }
  getScaleY() {
    return this._canvas.clientHeight === 0 ? 1 : this._canvas.height / this._canvas.clientHeight;
  }
  abstract getAdapterInfo(): any;
  abstract getFrameBufferSampleCount(): number;
  abstract isContextLost(): boolean;
  abstract getDrawingBufferWidth(): number;
  abstract getDrawingBufferHeight(): number;
  abstract getBackBufferWidth(): number;
  abstract getBackBufferHeight(): number;
  abstract getDeviceCaps(): Immutable<DeviceCaps>;
  abstract get clipSpaceZeroToOne(): boolean;
  abstract initContext(): Promise<void>;
  abstract clearFrameBuffer(
    clearColor: FrameBufferClearColors,
    clearDepth: Nullable<number>,
    clearStencil: Nullable<number>
  ): void;
  abstract createGPUTimer(): Nullable<ITimer>;
  abstract createRenderStateSet(): RenderStateSet;
  abstract createBlendingState(): BlendingState;
  abstract createColorState(): ColorState;
  abstract createRasterizerState(): RasterizerState;
  abstract createDepthState(): DepthState;
  abstract createStencilState(): StencilState;
  abstract createSampler(options: SamplerOptions): TextureSampler;
  abstract createTextureFromMipmapData<T extends BaseTexture>(
    data: TextureMipmapData,
    sRGB: boolean,
    options?: TextureCreationOptions
  ): Nullable<T>;
  abstract createTexture2D(
    format: TextureFormat,
    width: number,
    height: number,
    options?: TextureCreationOptions
  ): Nullable<Texture2D>;
  abstract createTexture2DFromImage(
    element: TextureImageElement,
    sRGB: boolean,
    options?: TextureCreationOptions
  ): Nullable<Texture2D>;
  abstract createTexture2DArray(
    format: TextureFormat,
    width: number,
    height: number,
    depth: number,
    options?: TextureCreationOptions
  ): Nullable<Texture2DArray>;
  abstract createTexture2DArrayFromImages(
    elements: TextureImageElement[],
    sRGB: boolean,
    options?: TextureCreationOptions
  ): Nullable<Texture2DArray>;
  abstract createTexture3D(
    format: TextureFormat,
    width: number,
    height: number,
    depth: number,
    options?: TextureCreationOptions
  ): Nullable<Texture3D>;
  abstract createCubeTexture(
    format: TextureFormat,
    size: number,
    options?: TextureCreationOptions
  ): Nullable<TextureCube>;
  abstract createTextureVideo(el: HTMLVideoElement, samplerOptions?: SamplerOptions): TextureVideo;
  abstract reverseVertexWindingOrder(reverse: boolean): void;
  abstract isWindingOrderReversed(): boolean;
  abstract copyTexture2D(src: Texture2D, srcLevel: number, dst: Texture2D, dstLevel: number): void;
  abstract copyFramebufferToTexture2D(src: FrameBuffer, index: number, dst: Texture2D, level: number): void;
  // program
  abstract createGPUProgram(params: GPUProgramConstructParams): GPUProgram;
  abstract createBindGroup(layout: Immutable<BindGroupLayout>): BindGroup;
  abstract createBuffer(sizeInBytes: number, options: BufferCreationOptions): GPUDataBuffer;
  abstract copyBuffer(
    sourceBuffer: GPUDataBuffer,
    destBuffer: GPUDataBuffer,
    srcOffset: number,
    dstOffset: number,
    bytes: number
  ): void;
  abstract createIndexBuffer(
    data: Uint16Array<ArrayBuffer> | Uint32Array<ArrayBuffer>,
    options?: BufferCreationOptions
  ): IndexBuffer;
  abstract createStructuredBuffer(
    structureType: PBStructTypeInfo,
    options: BufferCreationOptions,
    data?: TypedArray
  ): StructuredBuffer;
  abstract createVertexLayout(options: VertexLayoutOptions): VertexLayout;
  abstract createFrameBuffer(
    colorAttachments: BaseTexture[],
    depthAttachment: Nullable<BaseTexture>,
    options?: Nullable<FrameBufferOptions>
  ): FrameBuffer;
  // render related
  abstract setViewport(vp: Nullable<Immutable<number[] | DeviceViewport>>): void;
  abstract getViewport(): Immutable<DeviceViewport>;
  abstract setScissor(scissor: Nullable<Immutable<number[] | DeviceViewport>>): void;
  abstract getScissor(): Immutable<DeviceViewport>;
  abstract setProgram(program: Nullable<GPUProgram>): void;
  abstract getProgram(): Nullable<GPUProgram>;
  abstract setVertexLayout(vertexData: Nullable<VertexLayout>): void;
  abstract getVertexLayout(): Nullable<VertexLayout>;
  abstract setRenderStates(renderStates: Nullable<RenderStateSet>): void;
  abstract getRenderStates(): Nullable<RenderStateSet>;
  abstract getFramebuffer(): Nullable<FrameBuffer>;
  abstract setBindGroup(
    index: number,
    bindGroup: BindGroup,
    dynamicOffsets?: Nullable<Iterable<number>>
  ): void;
  abstract getBindGroup(index: number): [BindGroup, Nullable<Iterable<number>>];
  abstract flush(): void;
  abstract nextFrame(callback: () => void): number;
  abstract cancelNextFrame(handle: number): void;
  // misc
  abstract readPixels(
    index: number,
    x: number,
    y: number,
    w: number,
    h: number,
    buffer: TypedArray
  ): Promise<void>;
  abstract readPixelsToBuffer(
    index: number,
    x: number,
    y: number,
    w: number,
    h: number,
    buffer: GPUDataBuffer
  ): void;
  abstract beginCapture(): void;
  abstract endCapture(): RenderBundle;
  abstract looseContext(): void;
  abstract restoreContext(): void;
  protected abstract _executeRenderBundle(renderBundle: RenderBundle): number;
  protected abstract _draw(primitiveType: PrimitiveType, first: number, count: number): void;
  protected abstract _drawInstanced(
    primitiveType: PrimitiveType,
    first: number,
    count: number,
    numInstances: number
  ): void;
  protected abstract _compute(
    workgroupCountX: number,
    workgroupCountY: number,
    workgroupCountZ: number
  ): void;
  get backend() {
    return this._backend;
  }
  get videoMemoryUsage() {
    return this._gpuMemCost;
  }
  get frameInfo() {
    return this._frameInfo;
  }
  get isRendering() {
    return this._runningLoop !== null;
  }
  get canvas() {
    return this._canvas;
  }
  get type() {
    return this._backend.typeName();
  }
  get vSync() {
    return this._vSync;
  }
  set vSync(val) {
    this._vSync = !!val;
  }
  get fixedFrameTime() {
    return this._fixedFrameTime;
  }
  setFixedFrameTime(ms: Nullable<number>): void {
    const value = typeof ms === 'number' && Number.isFinite(ms) && ms > 0 ? ms : null;
    this._fixedFrameTime = value;
    // Seed the synthetic clock from the last frame timestamp so timing stays
    // continuous when enabled mid-run and starts at a reproducible epoch (0)
    // on a fresh device.
    this._fixedFrameClock = value !== null ? this._frameInfo.frameTimestamp : 0;
  }
  get pool() {
    return this._poolMap.get(this._defaultPoolKey)!;
  }
  get runLoopFunction() {
    return this._runLoopFunc;
  }
  get programBuilder() {
    return this._programBuilder;
  }
  poolExists(key: string | symbol) {
    return this._poolMap.has(key);
  }
  getPool(key: string | symbol) {
    let pool = this._poolMap.get(key);
    if (!pool) {
      pool = new Pool(this, key);
      this._poolMap.set(key, pool);
    }
    return pool;
  }
  setFont(fontName: Nullable<string>) {
    DrawText.setFont(this, fontName);
  }
  setTextRenderStates(renderStates: Nullable<RenderStateSet>) {
    DrawText.setRenderStates(renderStates);
  }
  drawText(text: string, x: number, y: number, color: string | Vector3 | Vector4): void;
  drawText(
    text: string,
    rect: Immutable<Rect>,
    color: string | Vector3 | Vector4,
    options?: DrawTextLayoutOptions
  ): void;
  drawText(
    text: string,
    xOrRect: number | Immutable<Rect>,
    yOrColor: number | string | Vector3 | Vector4,
    colorOrOptions?: string | Vector3 | Vector4 | DrawTextLayoutOptions,
    options?: DrawTextLayoutOptions
  ) {
    if (typeof xOrRect === 'number') {
      DrawText.drawText(
        this,
        text,
        colorOrOptions as string | Vector3 | Vector4,
        xOrRect,
        yOrColor as number
      );
    } else {
      DrawText.drawText(
        this,
        text,
        yOrColor as string | Vector3 | Vector4,
        xOrRect,
        options ?? (colorOrOptions as DrawTextLayoutOptions)
      );
    }
  }
  setFramebuffer(rt: Nullable<FrameBuffer>): void;
  setFramebuffer(color: BaseTexture[], depth?: BaseTexture, sampleCount?: number): void;
  setFramebuffer(
    colorOrRT: BaseTexture[] | Nullable<FrameBuffer>,
    depth?: BaseTexture,
    sampleCount?: number
  ) {
    let newRT: Nullable<FrameBuffer> = null;
    let temporal = false;
    if (!Array.isArray(colorOrRT)) {
      newRT = colorOrRT ?? null;
    } else {
      newRT = this.pool!.fetchTemporalFramebuffer(false, 0, 0, colorOrRT, depth, true, sampleCount);
      temporal = true;
    }
    const currentRT = this.getFramebuffer();
    if (currentRT !== newRT) {
      if (this._temporalFramebuffer && currentRT) {
        this.pool!.releaseFrameBuffer(currentRT);
      }
      this._temporalFramebuffer = temporal;
      this._setFramebuffer(newRT);
    }
  }
  disposeObject(obj: GPUObject, remove = true) {
    if (obj) {
      if (remove) {
        this.removeGPUObject(obj);
      }
      if (this.isContextLost()) {
        obj.destroy();
      } else {
        this._disposeObjectList.push(obj);
      }
    }
  }
  restoreObject(obj: GPUObject) {
    if (obj && obj.disposed && !this.isContextLost()) {
      obj.restore();
      obj.restoreHandler?.(obj);
    }
  }
  enableGPUTimeRecording(enable: boolean) {
    if (enable && !this._gpuTimer) {
      this._gpuTimer = this.createGPUTimer();
    } else if (!enable) {
      this._gpuTimer?.end();
      this._gpuTimer = null;
    }
  }
  beginTimestampQuery(_label?: string, _options?: TimestampQueryOptions): number {
    return 0;
  }
  endTimestampQuery(_id: number): void {
    return;
  }
  pollTimestampQuery(query: TimestampQueryHandle): Nullable<TimestampQueryResult> {
    return this.createUnsupportedTimestampQueryResult(query);
  }
  resolveTimestampQuery(query: TimestampQueryHandle): Promise<TimestampQueryResult> {
    return Promise.resolve(this.createUnsupportedTimestampQueryResult(query));
  }
  collectTimestampQueries(_filter?: TimestampQueryFilter): TimestampQueryResult[] {
    return [];
  }
  beginFrame() {
    if (this._beginFrameCounter === 0) {
      for (const obj of this._disposeObjectList) {
        obj.destroy();
      }
      this._disposeObjectList = [];
    }
    this._beginFrameCounter++;
    this._beginFrameTime = this._cpuTimer.now();
    this.updateFrameInfo();
    this._poolMap.forEach((pool) => pool.autoRelease());
    return this.onBeginFrame();
  }
  endFrame() {
    if (this._beginFrameCounter > 0) {
      this._beginFrameCounter--;
      if (this._beginFrameCounter === 0) {
        this._endFrameTime = this._cpuTimer.now();
        this._frameInfo.frameCounter++;
        this.onEndFrame();
      }
    }
  }
  getVertexAttribFormat(semantic: VertexSemantic, dataType: DataType, componentCount: number) {
    return getVertexAttribFormat(semantic, dataType, componentCount);
  }
  createInterleavedVertexBuffer(
    attribFormats: VertexAttribFormat[],
    data: TypedArray,
    options?: BufferCreationOptions
  ) {
    if (options && options.usage && options.usage !== 'vertex') {
      console.error(`createInterleavedVertexBuffer() failed: options.usage must be 'vertex' or not set`);
      return null;
    }
    let size = 0;
    for (const format of attribFormats) {
      size += getVertexFormatSize(format);
    }
    const vertexBufferType = makeVertexBufferType((data.byteLength / size) >> 0, ...attribFormats);
    if (!vertexBufferType) {
      console.error('createInterleavedVertexBuffer() failed: Cannot determine vertex buffer type');
      return null;
    }
    const opt = Object.assign(
      {
        usage: 'vertex',
        dynamic: false,
        managed: true,
        storage: false
      },
      options || {}
    );
    if (opt.storage) {
      opt.dynamic = false;
      opt.managed = false;
    }
    if (opt.dynamic) {
      opt.managed = false;
    }
    return this.createStructuredBuffer(vertexBufferType, opt, data);
  }
  createVertexBuffer(attribFormat: VertexAttribFormat, data: TypedArray, options?: BufferCreationOptions) {
    if (options && options.usage && options.usage !== 'vertex') {
      console.error(`createVertexBuffer() failed: options.usage must be 'vertex' or not set`);
      return null;
    }
    const count = getVertexFormatSize(attribFormat);
    const vertexBufferType = makeVertexBufferType((data.byteLength / count) >> 0, attribFormat);
    if (!vertexBufferType) {
      console.error('createInterleavedVertexBuffer() failed: Cannot determine vertex buffer type');
      return null;
    }
    const opt = Object.assign(
      {
        usage: 'vertex',
        dynamic: false,
        managed: true,
        storage: false
      },
      options || {}
    );
    if (opt.storage) {
      opt.dynamic = false;
      opt.managed = false;
    }
    if (opt.dynamic) {
      opt.managed = false;
    }
    return this.createStructuredBuffer(vertexBufferType, opt, data);
  }
  draw(primitiveType: PrimitiveType, first: number, count: number) {
    this._frameInfo.drawCalls++;
    this._draw(primitiveType, first, count);
  }
  drawInstanced(primitiveType: PrimitiveType, first: number, count: number, numInstances: number) {
    this._frameInfo.drawCalls++;
    this._drawInstanced(primitiveType, first, count, numInstances);
  }
  executeRenderBundle(renderBundle: RenderBundle): void {
    this._frameInfo.drawCalls += this._executeRenderBundle(renderBundle);
  }
  compute(workgroupCountX: number, workgroupCountY: number, workgroupCountZ: number) {
    this._frameInfo.computeCalls++;
    this._compute(workgroupCountX, workgroupCountY, workgroupCountZ);
  }
  runNextFrame(f: () => void) {
    if (f) {
      this._frameInfo.nextFrameCall.push(f);
    }
  }
  async runNextFrameAsync(f: () => void | Promise<void>) {
    return new Promise<void>((resolve) => {
      if (f) {
        this._frameInfo.nextFrameCall.push(() => {
          const p = f();
          if (p instanceof Promise) {
            p.then(() => resolve());
          } else {
            resolve();
          }
        });
      } else {
        resolve();
      }
    });
  }
  exitLoop() {
    if (this._runningLoop !== null) {
      if (this._runningLoop !== 0) {
        cancelAnimationFrame(this._runningLoop);
      } else {
        this.cancelNextFrame(this._runningLoop);
      }
      this._runningLoop = null;
    }
  }
  runLoop(func: (device: AbstractDevice) => void) {
    if (this._runningLoop !== null) {
      console.error('Device.runLoop() can not be nested');
      return;
    }
    if (!func) {
      console.error('Device.runLoop() argment error');
      return;
    }
    const that = this;
    that._runLoopFunc = func;
    (function entry() {
      if (that._vSync) {
        that._runningLoop = requestAnimationFrame(entry);
      } else {
        that._runningLoop = that.nextFrame(() => {
          if (that._runningLoop !== null) {
            entry();
          }
        });
      }
      if (that.beginFrame()) {
        that._runLoopFunc(that as unknown as AbstractDevice);
        that.endFrame();
      }
    })();
  }
  pushDeviceStates() {
    this._stateStack.push({
      windowOrderReversed: this.isWindingOrderReversed(),
      framebuffer: this.getFramebuffer(),
      viewport: this.getViewport(),
      scissor: this.getScissor(),
      program: this.getProgram(),
      renderStateSet: this.getRenderStates(),
      vertexLayout: this.getVertexLayout(),
      bindGroups: [this.getBindGroup(0), this.getBindGroup(1), this.getBindGroup(2), this.getBindGroup(3)]
    });
  }
  popDeviceStates() {
    if (this._stateStack.length === 0) {
      console.error('Device.popDeviceStates(): stack is empty');
    } else {
      const top = this._stateStack.pop()!;
      this.setFramebuffer(top.framebuffer);
      this.setViewport(top.viewport);
      this.setScissor(top.scissor);
      this.setProgram(top.program);
      this.setRenderStates(top.renderStateSet);
      this.setVertexLayout(top.vertexLayout);
      this.setBindGroup(0, ...top.bindGroups[0]);
      this.setBindGroup(1, ...top.bindGroups[1]);
      this.setBindGroup(2, ...top.bindGroups[2]);
      this.setBindGroup(3, ...top.bindGroups[3]);
      this.reverseVertexWindingOrder(top.windowOrderReversed);
    }
  }
  getGPUObjects() {
    return this._gpuObjectList;
  }
  getGPUObjectById(uid: number) {
    for (const list of [
      this._gpuObjectList.textures,
      this._gpuObjectList.samplers,
      this._gpuObjectList.buffers,
      this._gpuObjectList.framebuffers,
      this._gpuObjectList.programs,
      this._gpuObjectList.vertexArrayObjects
    ]) {
      for (const obj of list) {
        if (obj.uid === uid) {
          return obj;
        }
      }
    }
    return null;
  }
  screenXToDevice(val: number) {
    return this.getFramebuffer() ? val : Math.round(val * this.getScaleX());
  }
  deviceXToScreen(val: number) {
    return this.getFramebuffer() ? val : Math.round(val / this.getScaleX());
  }
  screenYToDevice(val: number) {
    return this.getFramebuffer() ? val : Math.round(val * this.getScaleY());
  }
  deviceYToScreen(val: number) {
    return this.getFramebuffer() ? val : Math.round(val / this.getScaleY());
  }
  buildRenderProgram(options: PBRenderOptions) {
    return this._programBuilder.buildRenderProgram(options);
  }
  buildComputeProgram(options: PBComputeOptions) {
    return this._programBuilder.buildComputeProgram(options);
  }
  getGPUObjectStack(obj: GPUObject): string {
    return this._gpuObjectList.stacks.get(obj) ?? '';
  }
  addGPUObject(obj: GPUObject) {
    const list = this.getGPUObjectList(obj);
    if (list && list.indexOf(obj) < 0) {
      list.push(obj);
      this._gpuObjectList.stacks.set(obj, new Error().stack ?? '');
      this.dispatchEvent('gpuobject_added', obj);
    }
  }
  removeGPUObject(obj: GPUObject) {
    const list = this.getGPUObjectList(obj);
    if (list) {
      const index = list.indexOf(obj);
      if (index >= 0) {
        list.splice(index, 1);
        this._gpuObjectList.stacks.delete(obj);
        this.dispatchEvent('gpuobject_removed', obj);
      }
    }
  }
  updateVideoMemoryCost(delta: number) {
    this._gpuMemCost += delta;
  }
  protected abstract onBeginFrame(): boolean;
  protected abstract onEndFrame(): void;
  protected abstract _setFramebuffer(fb: Nullable<FrameBuffer>): void;
  protected abstract _handleResize(
    cssWidth: number,
    cssHeight: number,
    deviceWidth: number,
    deviceHeight: number
  ): void;
  protected async initResizer() {
    return new Promise<void>((resolve) => {
      this.once(
        'resize',
        (cssWidth: number, cssHeight: number, deviceWidth: number, deviceHeight: number) => {
          this._handleResize(cssWidth, cssHeight, deviceWidth, deviceHeight);
          this.on(
            'resize',
            (cssWidth: number, cssHeight: number, deviceWidth: number, deviceHeight: number) => {
              this._handleResize(cssWidth, cssHeight, deviceWidth, deviceHeight);
            }
          );
          resolve();
        }
      );
      this._resizer.init();
    });
  }
  private updateFrameInfo() {
    this._frameInfo.drawCalls = 0;
    this._frameInfo.computeCalls = 0;
    let now = this._beginFrameTime;
    if (this._fixedFrameTime !== null) {
      this._fixedFrameClock += this._fixedFrameTime;
      now = this._fixedFrameClock;
    }
    if (this._frameInfo.frameTimestamp === 0) {
      this._frameInfo.frameTimestamp = now;
      this._frameInfo.elapsedTimeCPU = 0;
      this._frameInfo.elapsedTimeGPU = 0;
      this._frameInfo.elapsedFrame = 0;
      this._frameInfo.elapsedOverall = 0;
      this._frameInfo.FPS = 0;
      this._fpsCounter.time = now;
      this._fpsCounter.frame = this._frameInfo.frameCounter;
    } else {
      this._frameInfo.elapsedFrame = now - this._frameInfo.frameTimestamp;
      this._frameInfo.elapsedOverall += this._frameInfo.elapsedFrame;
      let cpuTime = 0;
      if (this._endFrameTime !== 0) {
        cpuTime = this._endFrameTime - this._frameInfo.frameTimestamp;
      }
      this._frameInfo.frameTimestamp = now;
      if (now >= this._fpsCounter.time + 1000) {
        this._frameInfo.FPS =
          ((this._frameInfo.frameCounter - this._fpsCounter.frame) * 1000) / (now - this._fpsCounter.time);
        this._fpsCounter.time = now;
        this._fpsCounter.frame = this._frameInfo.frameCounter;
        this._frameInfo.elapsedTimeCPU = cpuTime;
      }
    }
    const tmp = this._frameInfo.nextFrameCall;
    this._frameInfo.nextFrameCall = this._frameInfo.nextFrameCallNext;
    this._frameInfo.nextFrameCallNext = tmp;
    for (const f of this._frameInfo.nextFrameCallNext) {
      f();
    }
    this._frameInfo.nextFrameCallNext.length = 0;
  }
  private getGPUObjectList(obj: GPUObject) {
    let list: Nullable<GPUObject[]> = null;
    if (obj.isTexture()) {
      list = this._gpuObjectList.textures;
    } else if (obj.isSampler()) {
      list = this._gpuObjectList.samplers;
    } else if (obj.isBuffer()) {
      list = this._gpuObjectList.buffers;
    } else if (obj.isFramebuffer()) {
      list = this._gpuObjectList.framebuffers;
    } else if (obj.isProgram()) {
      list = this._gpuObjectList.programs;
    } else if (obj.isVertexLayout()) {
      list = this._gpuObjectList.vertexArrayObjects;
    } else if (obj.isBindGroup()) {
      list = this._gpuObjectList.bindGroups;
    }
    return list;
  }
  private createUnsupportedTimestampQueryResult(query: TimestampQueryHandle): TimestampQueryResult {
    return {
      id: typeof query === 'number' ? query : 0,
      label: typeof query === 'string' ? query : '',
      frameId: this._frameInfo.frameCounter,
      durationMs: 0,
      start: 0n,
      end: 0n,
      status: 'unsupported',
      message: 'GPU timestamp queries are not supported by this device'
    };
  }
  protected invalidateAll() {
    for (const list of [
      this._gpuObjectList.buffers,
      this._gpuObjectList.textures,
      this._gpuObjectList.samplers,
      this._gpuObjectList.programs,
      this._gpuObjectList.framebuffers,
      this._gpuObjectList.vertexArrayObjects,
      this._gpuObjectList.bindGroups
    ]) {
      for (const obj of list) {
        this.disposeObject(obj, false);
      }
    }
    if (this.isContextLost()) {
      for (const obj of this._disposeObjectList) {
        obj.destroy();
      }
      this._disposeObjectList = [];
    }
  }
  protected reloadAll() {
    for (const list of [
      this._gpuObjectList.buffers,
      this._gpuObjectList.textures,
      this._gpuObjectList.samplers,
      this._gpuObjectList.programs,
      this._gpuObjectList.framebuffers,
      this._gpuObjectList.vertexArrayObjects,
      this._gpuObjectList.bindGroups
    ]) {
      // obj.reload() may change the list, so make a copy first
      for (const obj of list.slice()) {
        obj.reload();
      }
    }
    return;
  }
  protected parseTextureOptions(options?: TextureCreationOptions) {
    const noMipmapFlag = options?.mipmapping === false ? GPUResourceUsageFlags.TF_NO_MIPMAP : 0;
    const writableFlag = options?.writable ? GPUResourceUsageFlags.TF_WRITABLE : 0;
    const dynamicFlag = options?.dynamic ? GPUResourceUsageFlags.DYNAMIC : 0;
    if (noMipmapFlag && options?.samplerOptions) {
      options.samplerOptions.mipFilter = 'none';
    }
    return noMipmapFlag | writableFlag | dynamicFlag;
  }
  protected parseBufferOptions(options?: BufferCreationOptions, defaultUsage?: BufferUsage) {
    options = options ?? {};
    const usage = options.usage ?? defaultUsage;
    let usageFlag: number;
    switch (usage) {
      case 'uniform':
        usageFlag = GPUResourceUsageFlags.BF_UNIFORM;
        options.managed = false;
        options.dynamic = options.dynamic ?? true;
        break;
      case 'vertex':
        usageFlag = GPUResourceUsageFlags.BF_VERTEX;
        break;
      case 'index':
        usageFlag = GPUResourceUsageFlags.BF_INDEX;
        break;
      case 'read':
        usageFlag = GPUResourceUsageFlags.BF_READ;
        options.managed = false;
        break;
      case 'write':
        usageFlag = GPUResourceUsageFlags.BF_WRITE;
        options.managed = false;
        break;
      case 'pack-pixel':
        usageFlag = GPUResourceUsageFlags.BF_PACK_PIXEL;
        options.managed = false;
        break;
      case 'unpack-pixel':
        usageFlag = GPUResourceUsageFlags.BF_UNPACK_PIXEL;
        options.managed = false;
        break;
      default:
        usageFlag = 0;
        break;
    }
    const storageFlag = (options?.storage ?? false) ? GPUResourceUsageFlags.BF_STORAGE : 0;
    const dynamicFlag = (options?.dynamic ?? false) ? GPUResourceUsageFlags.DYNAMIC : 0;
    const managedFlag = dynamicFlag === 0 && (options?.managed ?? true) ? GPUResourceUsageFlags.MANAGED : 0;
    return usageFlag | storageFlag | dynamicFlag | managedFlag;
  }
}

/**
 * Merge event target interface
 * @public
 */
export interface BaseDevice extends IEventTarget<DeviceEventMap> {}
